import React, { useState, useEffect } from 'react';
import {Link} from 'react-router-dom';
import WorkflowCascadeEditor,{blankCascade,validCascade} from '../components/WorkflowCascadeEditor';
import { automationsAPI } from '../services/api';
import { Plus, Trash2, Power, PowerOff, Zap, Clock, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import EmailEditor from '../components/EmailEditor';
import toast from 'react-hot-toast';

const LOG_LABELS={pending:'В очереди',sending:'Отправляется',waiting:'Ожидание следующего канала',sent:'Есть принятая отправка',failed:'Ошибка',unknown:'Результат неизвестен',cancelled:'Отменено',policy_skipped:'Пропущено ограничениями'};
const CHANNEL_LABELS = { sms: 'SMS', email: 'Email', telegram: 'Telegram',cascade:'Боты → Email → SMS' };

const TRIGGER_CONFIG_FIELDS = {
  segment_enter: [{ key: 'segment', label: 'Сегмент', type: 'text', placeholder: 'Спящие' }],
  segment_leave: [{ key: 'segment', label: 'Сегмент', type: 'text', placeholder: 'VIP' }],
  tier_upgrade: [
    { key: 'from_tier', label: 'С уровня', type: 'text', placeholder: 'silver' },
    { key: 'to_tier', label: 'На уровень', type: 'text', placeholder: 'gold' },
  ],
  purchase: [{ key: 'min_amount', label: 'Мин. сумма заказа', type: 'number', placeholder: '0' }],
  bonus_expiring: [{ key: 'days_before', label: 'За сколько дней до сгорания', type: 'number', placeholder: '7' }],
  birthday: [{ key: 'days_before', label: 'За сколько дней до ДР', type: 'number', placeholder: '1' }],
  cart_abandon: [{ key: 'min_cart_total', label: 'Мин. сумма корзины', type: 'number', placeholder: '0' }],
  product_view_abandon: [
    { key: 'min_views', label: 'Мин. просмотров', type: 'number', placeholder: '3' },
    { key: 'no_purchase_hours', label: 'Часов без покупки', type: 'number', placeholder: '24' },
  ],
  schedule: [
    { key: 'frequency', label: 'Частота', type: 'select', options: ['daily', 'weekly', 'monthly'] },
    { key: 'time', label: 'Время', type: 'time', placeholder: '10:00' },
  ],
};

const DELAY_PRESETS = [
  { value: 0, label: 'Сразу' },
  { value: 30, label: '30 мин' },
  { value: 60, label: '1 час' },
  { value: 180, label: '3 часа' },
  { value: 1440, label: '1 день' },
  { value: 4320, label: '3 дня' },
];

function AutomationFormModal({ triggers, automation, onClose, onSaved }) {
  const isEdit = !!automation;
  const [form, setForm] = useState({
    delivery_plan:automation?.delivery_plan||{...blankCascade},
    name: automation?.name || '',
    trigger_type: automation?.trigger_type || '',
    channel: automation?.channel || 'sms',
    message_text: automation?.message_text || '',
    email_subject: automation?.email_subject || '',
    delay_minutes: automation?.delay_minutes ?? 0,
    goal_type: automation?.goal_type || '',
    goal_period_days: automation?.goal_period_days || '',
  });
  const [triggerConfig, setTriggerConfig] = useState(automation?.trigger_config || {});
  const [loading, setLoading] = useState(false);

  const configFields = TRIGGER_CONFIG_FIELDS[form.trigger_type] || [];
  const grouped = {};
  triggers.forEach(t => { if (!grouped[t.group]) grouped[t.group] = []; grouped[t.group].push(t); });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.trigger_type) { toast.error('Выберите триггер'); return; }
    if (form.channel==='cascade'&&!validCascade(form.delivery_plan)) {toast.error('Заполните тексты каскада и ожидание');return;}
    if (form.channel!=='cascade'&&!form.message_text) { toast.error('Введите текст'); return; }
    setLoading(true);
    try {
      const data = { ...form,delivery_plan:form.channel==='cascade'?form.delivery_plan:null,message_text:form.channel==='cascade'?form.delivery_plan.bots_text:form.message_text, trigger_config: Object.keys(triggerConfig).length ? triggerConfig : null,
        goal_type: form.goal_type || null, goal_period_days: form.goal_period_days ? parseInt(form.goal_period_days) : null };
      if (isEdit) { await automationsAPI.update(automation.id, data); toast.success('Сохранено'); }
      else { await automationsAPI.create(data); toast.success('Создано'); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: ['email','cascade'].includes(form.channel) ? 900 : 580, maxHeight: '90vh', overflow: 'auto' }}>
        <h2 className="modal-title"><Zap size={18} style={{ color: 'var(--warning)', marginRight: 8 }} /> {isEdit ? 'Редактировать' : 'Новая автоматизация'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Название *</label>
            <input className="form-input" required placeholder="Напоминание о брошенной корзине"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Триггер (событие) *</label>
            <select className="form-input" value={form.trigger_type} disabled={isEdit}
              onChange={e => { setForm({ ...form, trigger_type: e.target.value }); setTriggerConfig({}); }}>
              <option value="">Выберите событие...</option>
              {Object.entries(grouped).map(([group, items]) => (
                <optgroup key={group} label={`${group}`}>
                  {items.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
                </optgroup>
              ))}
            </select>
            {isEdit && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Триггер нельзя изменить после создания</div>}
          </div>
          {configFields.length > 0 && (
            <div style={{ padding: 14, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10 }}>Настройки триггера</div>
              <div style={{ display: 'grid', gridTemplateColumns: configFields.length > 1 ? '1fr 1fr' : '1fr', gap: 10 }}>
                {configFields.map(f => (
                  <div className="form-group" key={f.key} style={{ marginBottom: 0 }}>
                    <label className="form-label">{f.label}</label>
                    {f.type === 'select' ? (
                      <select className="form-input" value={triggerConfig[f.key] || ''}
                        onChange={e => setTriggerConfig({ ...triggerConfig, [f.key]: e.target.value })}>
                        <option value="">—</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className="form-input" type={f.type === 'number' ? 'number' : f.type === 'time' ? 'time' : 'text'}
                        placeholder={f.placeholder} value={triggerConfig[f.key] || ''}
                        onChange={e => setTriggerConfig({ ...triggerConfig, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Задержка отправки</label>
              <select className="form-input" value={form.delay_minutes}
                onChange={e => setForm({ ...form, delay_minutes: parseInt(e.target.value) })}>
                {DELAY_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Канал</label>
              <select className="form-input" value={form.channel}
                onChange={e => setForm({ ...form, channel: e.target.value })}>
                <option value="sms">SMS</option>
                <option value="email">Email</option><option value="cascade">Боты → Email → SMS</option>
              </select>
            </div>
          </div>
          {form.channel==='cascade'?<WorkflowCascadeEditor value={form.delivery_plan} onChange={delivery_plan=>setForm({...form,delivery_plan})}/>:form.channel === 'email' ? (
            <EmailEditor
              value={form.message_text}
              onChange={v => setForm({ ...form, message_text: v })}
              subject={form.email_subject}
              onSubjectChange={v => setForm({ ...form, email_subject: v })}
              channel="email"
            />
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Текст сообщения *</label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, lineHeight: 1.6 }}>
                  Переменные: <code>{'{name}'}</code> <code>{'{phone}'}</code> <code>{'{balance}'}</code> <code>{'{tier}'}</code>
                </div>
                <textarea className="form-input" rows={4} required placeholder="{name}, вы забыли товары в корзине!"
                  value={form.message_text} onChange={e => setForm({ ...form, message_text: e.target.value })} style={{ resize: 'vertical' }} />
              </div>
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Цель (конверсия)</label>
              <select className="form-input" value={form.goal_type} onChange={e => setForm({ ...form, goal_type: e.target.value })}>
                <option value="">Без цели</option>
                <option value="purchase">Покупка</option>
                {form.channel === 'email' && <option value="email_open">Открытие email</option>}
                {form.channel === 'email' && <option value="link_click">Клик по ссылке</option>}
              </select>
            </div>
            {form.goal_type && (
              <div className="form-group">
                <label className="form-label">Период (дней)</label>
                <input className="form-input" type="number" min="1" placeholder="7"
                  value={form.goal_period_days} onChange={e => setForm({ ...form, goal_period_days: e.target.value })} />
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '...' : isEdit ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AutomationsPage() {
  const [automations, setAutomations] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAuto, setEditingAuto] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [logs, setLogs] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([automationsAPI.list(), automationsAPI.triggers()])
      .then(([aRes, tRes]) => { setAutomations(aRes.data); setTriggers(tRes.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleToggle = async (a) => {
    try { await automationsAPI.update(a.id, { is_active: !a.is_active }); toast.success(a.is_active ? 'Отключена' : 'Включена'); load(); }
    catch { toast.error('Ошибка'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Удалить автоматизацию?')) return;
    try { await automationsAPI.delete(id); toast.success('Удалено'); load(); }
    catch { toast.error('Ошибка'); }
  };
  const loadLogs = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    try { const res = await automationsAPI.logs(id); setLogs({ ...logs, [id]: res.data }); setExpandedId(id); }
    catch { toast.error('Ошибка'); }
  };
  const getTriggerLabel = (type) => { const t = triggers.find(t => t.type === type); return t ? t.label : type; };
  const formatDelay = (m) => { if (!m) return 'Сразу'; if (m >= 1440) return `${Math.round(m/1440)} дн`; if (m >= 60) return `${Math.round(m/60)} ч`; return `${m} мин`; };
  const getCR = (a) => {
    if (!a.goal_type) return '—';
    if (!a.total_sent) return '0%';
    return `${Math.round(a.conversions / a.total_sent * 100)}%`;
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка...</div>;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Автоматические рассылки</h1>
        <p className="page-subtitle">Триггерные кампании · {automations.length} шт</p>
      </div>
      <div className="page-actions"><Link className="btn btn-ghost" to="/delivery-results?source_type=automation">Результаты шагов</Link>
        <button className="btn btn-primary" onClick={() => { setEditingAuto(null); setShowForm(true); }}>
          <Plus size={16} /> Создать
        </button>
        <button className="btn btn-ghost" onClick={async () => {
          try { await automationsAPI.runNow(); toast.success('Обработчик выполнен'); load(); }
          catch { toast.error('Ошибка'); }
        }}><Zap size={16} /> Запустить сейчас</button>
      </div>
      <div className="table-container">
        <table className="table">
          <thead><tr>
            <th></th><th>Название</th><th>Триггер</th><th>Канал</th><th>Задержка</th><th>Отправлено</th><th>CR</th><th>Статус</th><th>Создано</th><th></th>
          </tr></thead>
          <tbody>
            {automations.map(a => (
              <React.Fragment key={a.id}>
                <tr style={{ cursor: 'pointer' }} onClick={() => loadLogs(a.id)}>
                  <td><Zap size={16} style={{ color: a.is_active ? 'var(--warning)' : 'var(--text-muted)' }} /></td>
                  <td style={{ fontWeight: 600 }}>{a.name}{a.delivery_plan&&<Link style={{display:'block',fontSize:12,marginTop:6}} to={'/delivery-results?source_type=automation&source_id='+a.id} onClick={e=>e.stopPropagation()}>Каналы и результаты</Link>}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{getTriggerLabel(a.trigger_type)}</td>
                  <td><span className={`badge ${a.channel === 'email' ? 'badge-info' : 'badge-warning'}`}>{CHANNEL_LABELS[a.channel] || a.channel}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDelay(a.delay_minutes)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>
                    {a.total_sent}{a.total_errors > 0 && <span style={{ color: 'var(--danger)', fontSize: 11 }}> / {a.total_errors} ош.</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--success)' }}>{getCR(a)}</td>
                  <td><span className={`badge ${a.is_active ? 'badge-success' : 'badge-danger'}`}>{a.is_active ? 'Активна' : 'Выкл'}</span></td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleDateString('ru')}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" title="Редактировать" onClick={() => { setEditingAuto(a); setShowForm(true); }}><Edit2 size={14} /></button>
                      <button className="btn btn-ghost btn-sm" title={a.is_active ? 'Отключить' : 'Включить'} onClick={() => handleToggle(a)}>
                        {a.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                      </button>
                      <button className="btn btn-danger btn-sm" title="Удалить" onClick={() => handleDelete(a.id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
                {expandedId === a.id && logs[a.id] && (
                  <tr><td colSpan={10} style={{ padding: 0 }}>
                    <div style={{ padding: '12px 20px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
                      {logs[a.id].items.length > 0 ? (<>
                        <table className="table" style={{ fontSize: 11 }}>
                          <thead><tr><th>Время</th><th>Событие</th><th>Статус</th><th>Ошибка</th></tr></thead>
                          <tbody>{logs[a.id].items.slice(0, 20).map(l => (
                            <tr key={l.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{new Date(l.created_at + 'Z').toLocaleString('ru')}</td>
                              <td>{l.trigger_event || '—'}</td>
                              <td><span className={`badge ${l.status === 'sent' ? 'badge-success' : l.status === 'failed' ? 'badge-danger' : 'badge-info'}`}>{LOG_LABELS[l.status]||l.status}</span></td>
                              <td style={{ color: 'var(--danger)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.error || '—'}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, fontSize: 11 }} onClick={async () => {
                          try { await automationsAPI.clearLogs(a.id); toast.success('Логи очищены'); loadLogs(a.id); load(); }
                          catch { toast.error('Ошибка'); }
                        }}>Сбросить логи</button>
                      </>) : <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: 10 }}>Отправок пока нет</div>}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {automations.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Автоматизаций нет. Создайте первую триггерную рассылку.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showForm && <AutomationFormModal triggers={triggers} automation={editingAuto}
        onClose={() => { setShowForm(false); setEditingAuto(null); }} onSaved={load} />}
    </div>
  );
}